import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/TrainingView.css';
import StreakUpgradeButton from '../../components/StreakUpgradeButton';
import LockedTrainingModal from '../../components/modals/LockedTrainingModal';
import FreeTrainingsCompletedModal from '../../components/modals/FreeTrainingsCompletedModal';
import PricingModal from '../../components/modals/PricingModal';
import { API_URL } from '../../app/constants';
import { useSessionCreation } from '../../contexts/SessionCreationContext';
// Stats component removed

const TrainingView = () => {
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showFreeTrainingsCompletedModal, setShowFreeTrainingsCompletedModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  // Session creation status
  const { status: sessionStatus } = useSessionCreation();
  
  // Progress bar state
  const [progressPercent, setProgressPercent] = useState(0);
  const progressIntervalRef = useRef(null);
  const progressStartTimeRef = useRef(null);
  
  // Log session status changes for debugging
  useEffect(() => {
    console.log('TrainingView: sessionStatus changed to:', sessionStatus);
  }, [sessionStatus]);

  // Progress bar management
  useEffect(() => {
    if (sessionStatus === 'creating') {
      // Start progress bar
      setProgressPercent(0);
      progressStartTimeRef.current = Date.now();
      
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - progressStartTimeRef.current;
        const progressFromTime = Math.min((elapsed / 60000) * 100, 100); // 60 seconds = 100%
        setProgressPercent(progressFromTime);
        
        // If we reach 100% by time, stop the interval
        if (progressFromTime >= 100) {
          clearInterval(progressIntervalRef.current);
        }
      }, 100); // Update every 100ms for smooth animation
      
    } else if (sessionStatus === 'completed') {
      // Jump to 100% instantly and reload
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setProgressPercent(100);
      
      // Reload the page immediately after reaching 100%
      setTimeout(() => {
        window.location.reload();
      }, 200); // Small delay to show 100%
      
    } else {
      // Clean up if status changes to something else
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setProgressPercent(0);
    }
    
    // Cleanup on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [sessionStatus]);
  
  // Check user status using the same method as Sidebar.js and App.js
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const jwt = localStorage.getItem('jwt');
        const response = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: { 'Authorization': `Bearer ${jwt}` },
          method: "GET",
        });
        
        if (response.ok) {
          const userData = await response.json();
          console.log("User status from API:", userData.status);
          setUserStatus(userData.status);
        }
      } catch (error) {
        console.error("Error checking user status:", error);
      }
    };
    
    checkUserStatus();
  }, []);

  // Check for first time modal
  useEffect(() => {
    console.log('TrainingView mounted, checking for first time');
    const isFirstTime = localStorage.getItem('firstTimeStudyPlan');
    console.log('firstTimeStudyPlan flag:', isFirstTime);
    if (isFirstTime === 'true') {
      console.log('Showing first time modal');
      setShowFirstTimeModal(true);
      localStorage.removeItem('firstTimeStudyPlan');
    }
  }, []);

  useEffect(() => {
    // Fetch stages and their exams in a coordinated way
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const jwt = localStorage.getItem('jwt');

        // Fetch stages first
        const stagesResponse = await fetch(`${API_URL}/api/v1/panel/student/stages`, {
          headers: { 'Authorization': `Bearer ${jwt}` }
        });
        const stagesData = await stagesResponse.json();
        
        // Handle new API response structure
        const actualStages = stagesData.stages || stagesData; // Support both old and new format
        const needsDiagnostic = stagesData.needs_diagnostic || false;
        const currentLevel = stagesData.current_level;
        
        // If user needs diagnostic test, create diagnostic stage
        let modifiedStages = actualStages;
        if (needsDiagnostic && currentLevel && actualStages.length === 0) {
          modifiedStages = [{
            stage_id: 0,
            order: 0,
            is_completed: false,
            stage_type: "Diagnostic",
            name: "Diagnostic Test",
            session_explanation: "Take your diagnostic test to get personalized training sessions.",
            exams: [{
              exam_id: `diagnostic-${currentLevel}`,
              section: "diagnostic",
              completed: false,
              score: null,
              is_final_exam: false
            }]
          }];
          console.log(`Created diagnostic stage for level ${currentLevel}`);
        }
        
        
        setStages(modifiedStages);

        // Determine which stage to show and fetch its exams
        if (modifiedStages.length > 0) {
          const firstIncompleteIndex = modifiedStages.findIndex(stage => !stage.is_completed);
          const index = firstIncompleteIndex === -1 ? modifiedStages.length - 1 : firstIncompleteIndex;
          setSelectedStage(modifiedStages[index]);

          // If it's the diagnostic stage we created, use its exams directly
          if (modifiedStages[index].stage_id === 0 && modifiedStages[index].exams) {
            setExams(modifiedStages[index].exams);
          } else {
            // Fetch exams for regular stages
            const examsResponse = await fetch(`${API_URL}/api/v1/panel/student/stage/${modifiedStages[index].stage_id}/exams`, {
              headers: { 'Authorization': `Bearer ${jwt}` }
            });
            const examsData = await examsResponse.json();
            setExams(examsData);
          }
        } else {
          // No stages available, set empty exams
          setExams([]);
          // Don't set error if session creation is in progress
          if (sessionStatus !== 'creating') {
            setError("Error loading training data");
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        // Don't set error if session creation is in progress
        if (sessionStatus !== 'creating') {
          setError("Error loading training data");
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if userStatus is available (either 'active' or 'inactive')
    if (userStatus) {
      fetchInitialData();
    }
  }, [userStatus, sessionStatus]); // Depends on userStatus and sessionStatus

  const fetchExamsForStage = async (stageId) => {
    setLoading(true);
    setError(null);
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/student/stage/${stageId}/exams`, {
        headers: { 'Authorization': `Bearer ${jwt}` }
      });
      const data = await response.json();
      setExams(data);
    } catch (error) {
      console.error("Error fetching exams:", error);
      setError("Error fetching exams");
    } finally {
      setLoading(false);
    }
  };
  
  // Check if a stage is accessible (first stage is always accessible)
  const isStageAccessible = (stage, allStages) => {
    // Always return true for premium users - status should be 'active' for premium users
    // This matches what's used in settings/SettingsView.js
    if (userStatus === 'active') {
      console.log("User is premium (active), all stages accessible");
      return true;
    }
    
    if (!allStages || !allStages.length) return false;

    // Find the index of the current stage
    const index = allStages.findIndex(s => s.stage_id === stage.stage_id);
    
    return (
      index === 0 || // First stage is always accessible
      (index > 0 && allStages[index - 1].is_completed) // Previous stage must be completed
    );
  };

  // Check if Chapter 1 (first non-diagnostic stage) is completed
  const isChapter1Completed = () => {
    if (!stages || stages.length < 2) return false;
    // Chapter 1 is at index 1 (index 0 is diagnostic)
    return stages[1]?.is_completed || false;
  };

  const handleStageSelect = (stage) => {
    // Premium users can access all stages
    if (userStatus === 'active' || isStageAccessible(stage, stages)) {
      setSelectedStage(stage);
      fetchExamsForStage(stage.stage_id);
      setShowStageDropdown(false);
    } else {
      // Show locked training modal for inaccessible stages
      setShowLockedModal(true);
    }
  };

  // Handle upgrade click from free trainings completed modal
  const handleUpgradeClick = () => {
    setShowFreeTrainingsCompletedModal(false);
    setShowPricingModal(true);
  };
  
  const getStageName = (stage, index) => {
    if (index === 0) return "Diagnostic Test";
    // Check if it's a mock test by type or name
    if (stage.stage_type === "Mock Test" || (stage.name && stage.name.includes("Mock Test"))) {
      return stage.name;
    }
    return `Chapter ${index}`;
  };
  
  // Get exam type based on section and category
  const getExamType = (exam) => {
    // For diagnostic exams (stage index 0)
    if (stages.indexOf(selectedStage) === 0) {
      return "Diagnostic";
    }
    // For regular exams
    if (exam.section) {
      return exam.section.charAt(0).toUpperCase() + exam.section.slice(1);
    }
    if (exam.is_final_exam) return "Review";
    return "Exercise";
  };
  
  // Calculate progress percentage
  const getProgress = (exam) => {
    if (exam.completed) return 100;
    return 0;
  };
  
  // Handle diagnostic exam click - use API like Dashboard
  const handleDiagnosticExamClick = async (e, exam) => {
    e.preventDefault();
    
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
        method: "GET",
      });
      
      if (response.ok) {
        const userData = await response.json();
        const currentLevel = userData.current_level || userData.target_level || userData.level;
        
        console.log("DEBUG: User current level from API:", currentLevel);
        
        if (!currentLevel) {
          console.log("DEBUG: No current level found, navigating to target-level");
          window.location.href = '/target-level';
          return;
        }
        
        // Map user level to diagnostic exam IDs (110-114) - same as Dashboard
        const levelToExamId = {
          'N5': 110,
          'N4': 111,
          'N3': 112,
          'N2': 113,
          'N1': 114
        };
        
        const diagnosticExamId = levelToExamId[currentLevel] || 110; // Default to N5
        console.log("DEBUG: Navigating to diagnostic exam ID:", diagnosticExamId);
        window.location.href = `/diagnostic-exam/${diagnosticExamId}`;
      } else {
        console.error("Failed to fetch user data");
        window.location.href = '/target-level';
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      window.location.href = '/target-level';
    }
  };

  // Get exam path
  const getExamPath = (exam, stageIndex) => {
    // Check if the current stage is the diagnostic test stage
    // This is a safer approach than relying on index === 0
    const selectedStageIsFirst = stages.length > 0 && stages[0].stage_id === selectedStage?.stage_id;
    
    if (exam.completed) {
      return selectedStageIsFirst 
        ? `/review-personalized/diagnostic` 
        : `/review-personalized/${exam.exam_id}`;
    }
    
    return selectedStageIsFirst
      ? `/target-level` // Will be overridden by click handler for non-completed diagnostic exams
      : `/personalized-exam/${exam.exam_id}`;
  };


  return (
    <div className="training-view">
      <StreakUpgradeButton source="Training View" />
      <div className="dashboard-header">
        <h1>My Training</h1>
      </div>
      
      {!loading && stages.length > 0 && (
        <div className="session-selector">
          <button 
            className="session-button"
            onClick={() => setShowStageDropdown(!showStageDropdown)}
          >
            {sessionStatus === 'creating' ? "Creating your next session" : (selectedStage ? getStageName(selectedStage, stages.indexOf(selectedStage)) : "Select Chapter")}
            <svg className={`arrow-icon ${showStageDropdown ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className={`session-dropdown ${showStageDropdown ? 'show' : ''}`}>
            {[...stages].reverse().map((stage, index) => {
              // Reverse index for naming (diagnostic test should still be index 0)
              const originalIndex = stages.length - 1 - index;
              const accessible = isStageAccessible(stage, stages);

              return (
                <button
                  key={stage.stage_id}
                  className={`session-option ${selectedStage && selectedStage.stage_id === stage.stage_id ? 'active' : ''} ${!accessible && userStatus !== 'active' ? 'locked' : ''}`}
                  onClick={() => handleStageSelect(stage)}
                >
                  {getStageName(stage, originalIndex)}
                  {userStatus !== 'active' && !accessible && <span className="lock-icon">🔒</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Session Explanation */}
      {sessionStatus === 'creating' ? (
        <div className="session-explanation">
          <p>This should take 30 seconds 💜. If it doesn't reload automatically, please refresh the page.</p>
          
          {/* Progress Bar */}
          <div style={{
            marginTop: '16px',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px',
            height: '8px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div 
              style={{
                height: '100%',
                backgroundColor: '#9334E9',
                borderRadius: '8px',
                transition: 'width 0.1s linear',
                width: `${progressPercent}%`
              }}
            />
          </div>
          
          {/* Progress percentage text */}
          <div style={{
            textAlign: 'center',
            marginTop: '8px',
            fontSize: '14px',
            color: '#6B6B6B',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {Math.round(progressPercent)}%
          </div>
        </div>
      ) : selectedStage && selectedStage.session_explanation && (
        <div className="session-explanation">
          <p>{selectedStage.session_explanation}</p>
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner-ripple">
            <div></div>
            <div></div>
          </div>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : sessionStatus === 'creating' ? (
        // Show skeleton exercises while sessions are being created
        <>
          <div className="exercises-grid">
            {[1, 2, 3, 4].map((index) => (
              <div 
                key={`skeleton-${index}`}
                className="exercise-card"
                style={{
                  opacity: 0.7,
                  cursor: 'default',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Shimmer effect overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  animation: 'shimmer 2s infinite',
                  zIndex: 1
                }} />
                
                <div className="exercise-card-content">
                  <div className="exercise-info">
                    <span className="exercise-type" style={{ opacity: 0.6 }}>
                      Exercise {index}
                    </span>
                    <div style={{
                      height: '20px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '4px',
                      marginTop: '8px',
                      width: '80%',
                      animation: 'pulse 2s ease-in-out infinite'
                    }} />
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: '0%',
                        backgroundColor: '#E5E7EB'
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CSS for animations */}
          <style>{`
            @keyframes shimmer {
              0% { left: -100%; }
              100% { left: 100%; }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 0.4; }
              50% { opacity: 0.8; }
            }
          `}</style>
        </>
      ) : (
        <div className="exercises-grid">
          {exams.map((exam) => (
            <Link 
              key={exam.exam_id}
              onClick={(e) => {
                // Find the stage index
                const stageIndex = stages.indexOf(selectedStage);
                const selectedStageIsFirst = stages.length > 0 && stages[0].stage_id === selectedStage?.stage_id;
                
                // Add debug logging before navigation
                console.log('Link clicked:', {
                  exam_id: exam.exam_id,
                  path: getExamPath(exam, stageIndex),
                  stageIndex,
                  selectedStage,
                  userStatus,
                  isFirstStage: selectedStageIsFirst,
                  examCompleted: exam.completed
                });
                
                // Handle diagnostic exam click for non-completed exams on first stage
                if (selectedStageIsFirst && !exam.completed) {
                  handleDiagnosticExamClick(e, exam);
                  return;
                }
                
                // For free users, show appropriate modal when trying to access chapter 2+ trainings
                if (userStatus !== 'active' && stageIndex > 1) {
                  e.preventDefault();
                  
                  // If Chapter 1 is completed, show the "free trainings completed" modal
                  if (isChapter1Completed()) {
                    setShowFreeTrainingsCompletedModal(true);
                  } else {
                    // Otherwise show the regular locked modal
                    setShowLockedModal(true);
                  }
                  return false;
                }
                
                // For non-diagnostic exams, navigate directly
                // if (!selectedStageIsFirst && !exam.completed) {
                //   // Navigate directly without skeleton loader
                // }
              }}
              to={getExamPath(exam, stages.indexOf(selectedStage))}
              className="exercise-card"
            >
              <div className="exercise-card-content">
                <div className="exercise-info">
                  <span className="exercise-type">{getExamType(exam)}</span>
                  <h3 className="exercise-title">
                    {exam.categories || (exam.is_final_exam ? 'Quiz Review' : 'Exercise')}
                  </h3>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${getProgress(exam)}%` }}
                  />
                </div>
              </div>
            </Link>
          ))}
          
          {exams.length === 0 && !loading && sessionStatus !== 'creating' && (
            <div className="empty-state">No trainings available for this chapter</div>
          )}
        </div>
      )}

      
      {/* First Time Welcome Modal */}
      {showFirstTimeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }} onClick={() => setShowFirstTimeModal(false)}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px 28px',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              color: "#6B6B6B", 
              marginBottom: "8px",
              fontSize: '20px',
              fontWeight: '600',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Welcome!
            </h2>
            <div style={{
              fontSize: '24px', 
              marginBottom: '16px'
            }}>🎉</div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#333',
              marginBottom: '16px',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Your First Personalized Training
            </div>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '16px',
              lineHeight: '1.5',
              color: '#6B6B6B',
              fontFamily: 'Manrope, sans-serif',
              textAlign: 'center'
            }}>
              Welcome to your custom study plan! Based on your diagnostic results, we've created chapters and practice tests tailored just for you.
            </p>

            <button
              style={{
                backgroundColor: "#9334E9",
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: 'Manrope, sans-serif',
                cursor: 'pointer',
                width: '100%'
              }}
              onClick={() => setShowFirstTimeModal(false)}
            >
              Start Learning!
            </button>
          </div>
        </div>
      )}
      
      <LockedTrainingModal 
        isOpen={showLockedModal}
        onClose={() => setShowLockedModal(false)}
      />

      <FreeTrainingsCompletedModal 
        isOpen={showFreeTrainingsCompletedModal}
        onClose={() => setShowFreeTrainingsCompletedModal(false)}
        onUpgrade={handleUpgradeClick}
        source="Training"
      />

      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        source="Free Trainings Completed"
      />

    </div>
  );
};

export default TrainingView;